import React from "react";
import { Download, ArrowUpRight, Sparkles } from "lucide-react";

const RESPONSICLES = {
  image:
    "https://customer-assets.emergentagent.com/job_karam-journey/artifacts/57wx9pnt_WhatsApp%20Image%202026-01-22%20at%2017.29.00.jpeg",
  brochure:
    "https://customer-assets.emergentagent.com/job_karam-journey/artifacts/jbnbjci6_responsicles_elegant_brochure.pdf",
  tag: "Innovative Product · Trademark™",
  title: "Responsicles",
  subtitle: "A Multi-Purpose Classroom Engagement Tool",
  tagline:
    "Because every child deserves to be seen, heard, and inspired.",
  intro:
    "A thoughtful fusion of two powerful ideas — Responses and Popsicles — reimagined as colourful acrylic sticks that transform every classroom into an inclusive, joyful and fair learning space. A tool born from the conviction that fairness in the classroom isn't an ideal to aspire to — it's a practice to embody every single day.",
  kit: [
    { stat: "6", label: "Vibrant Colours", color: "#EF4444" },
    { stat: "60", label: "Unique Numbers", color: "#EC4899" },
    { stat: "10", label: "Distinct Shapes", color: "#10B981" },
    { stat: "60", label: "Affirmations", color: "#D97706" },
  ],
  uses: [
    "Equal Participation — turning passive observers into active contributors",
    "Attendance Tracking in under 30 seconds",
    "Instant Group Formation via colours & shapes",
    "Foundational Numeracy with numbers 1–60",
    "Vocabulary & Language Building",
    "Random Fair Task Allocation — zero favouritism",
    "SEL Affirmation Boosters aligned with NEP 2020",
    "Kinesthetic & Creative Learning",
    "Icebreakers for trainer & corporate sessions",
    "Workshop Facilitation aid",
  ],
  quote:
    "Responsicles is not just a product. It is a promise — that in our classrooms, every child will be seen.",
  audience: [
    "School Teachers · Primary to Senior Secondary",
    "Special Educators · Inclusive Settings",
    "Teacher Trainers & Facilitators",
    "Workshop & Corporate Trainers",
  ],
};

export default function Innovation() {
  return (
    <section
      id="innovation"
      data-testid="innovation-section"
      className="py-20 sm:py-28 relative overflow-hidden"
    >
      {/* Vibrant decorative blob */}
      <div
        aria-hidden
        className="absolute -top-32 right-0 h-[520px] w-[520px] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: "radial-gradient(circle, #EF4444 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-32 h-[480px] w-[480px] rounded-full opacity-[0.07] blur-3xl"
        style={{ background: "radial-gradient(circle, #10B981 0%, transparent 70%)" }}
      />

      <div className="container-prose relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <p className="overline flex items-center gap-2">
              <Sparkles size={14} className="text-saffron" />
              04 — Innovation
            </p>
            <h2 className="h2-display mt-4 text-4xl sm:text-6xl text-ink">
              {RESPONSICLES.tag.split("·")[0]} <br />
              <span className="italic text-saffron">{RESPONSICLES.title}</span>™
            </h2>
            <p
              className="mt-6 text-xl sm:text-2xl italic text-ink/80 max-w-2xl"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              "{RESPONSICLES.tagline}"
            </p>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="body-prose text-ink/75 text-base sm:text-lg">
              {RESPONSICLES.intro}
            </p>
          </div>
        </div>

        {/* Hero product image + kit stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-square w-full bg-ink/5 overflow-hidden">
              <img
                src={RESPONSICLES.image}
                alt="Responsicles — colourful classroom engagement sticks designed by Karamjeet Sodhi"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="responsicles-image"
              />
              <div className="absolute inset-0 ring-1 ring-ink/15 pointer-events-none" />
            </div>
            <div className="absolute -top-4 -left-4 h-20 w-20 sm:h-28 sm:w-28 border-2 border-saffron z-0" aria-hidden />
            <div className="absolute -bottom-4 -right-4 h-20 w-20 sm:h-28 sm:w-28 bg-magenta z-0" aria-hidden />
          </div>

          <div className="lg:col-span-6">
            <p className="overline text-saffron">The Kit</p>
            <h3
              className="mt-3 text-3xl sm:text-4xl text-ink"
              style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
            >
              {RESPONSICLES.subtitle}
            </h3>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {RESPONSICLES.kit.map((k, i) => (
                <div
                  key={i}
                  data-testid={`responsicles-stat-${i}`}
                  className="border border-ink-20 p-5 bg-ivory hover:bg-ivorySoft transition-colors"
                  style={{ borderColor: "rgba(30,27,75,0.18)" }}
                >
                  <p
                    className="text-5xl sm:text-6xl leading-none"
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 500,
                      color: k.color,
                    }}
                  >
                    {k.stat}
                  </p>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ink/70">
                    {k.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={RESPONSICLES.brochure}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="responsicles-brochure-cta"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-ink text-ivory text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-saffron hover:text-ink transition-colors"
              >
                <Download size={14} />
                Download Brochure
              </a>
              <a
                href="#contact"
                data-testid="responsicles-enquire-cta"
                className="inline-flex items-center gap-2 px-7 py-4 text-[12px] uppercase tracking-[0.22em] font-medium text-ink border-b border-ink/30 hover:border-saffron hover:text-saffron transition-colors"
              >
                Enquire for Schools
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>

        {/* 10 use cases */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="overline text-saffron">Sixty sticks · Endless possibilities</p>
            <h3
              className="mt-3 text-3xl sm:text-4xl text-ink"
              style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
            >
              Ten ways to <span className="italic text-saffron">transform</span> your classroom.
            </h3>
            <p className="mt-5 body-prose text-ink/70 text-base">
              Distribute. Respond. Return. The simplest workflow in the world — and an
              entire ecosystem of pedagogical moves it unlocks.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {RESPONSICLES.uses.map((u, i) => (
                <li
                  key={i}
                  data-testid={`responsicles-use-${i}`}
                  className="flex items-start gap-4 py-3 border-b border-ink-10"
                  style={{ borderColor: "rgba(30,27,75,0.12)" }}
                >
                  <span
                    className="text-2xl shrink-0 w-8"
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 500,
                      color: "#D97706",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="body-prose text-[15px] text-ink/80">{u}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Founder quote */}
        <div className="mt-20 bg-ink text-ivory p-10 sm:p-14 relative overflow-hidden">
          <span
            aria-hidden
            className="absolute -top-8 left-6 text-[160px] leading-none text-saffron/25 select-none"
            style={{ fontFamily: '"Cormorant Garamond", serif' }}
          >
            “
          </span>
          <div className="relative max-w-3xl">
            <p
              className="text-2xl sm:text-4xl italic leading-snug text-ivory"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
              data-testid="responsicles-quote"
            >
              {RESPONSICLES.quote}
            </p>
            <p className="mt-6 text-[12px] uppercase tracking-[0.22em] text-saffron">
              — Karamjeet Sodhi · Creator of Responsicles™
            </p>
          </div>

          <div className="mt-10 pt-8 border-t border-ivory/15 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            <p className="overline text-ivory/60 sm:col-span-2 mb-2">Built for</p>
            {RESPONSICLES.audience.map((a, i) => (
              <p key={i} className="text-[14px] body-prose text-ivory/80">
                · {a}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
